import React, { useState, useEffect } from 'react';
import { Container, Card, Table, Button, Modal, Form, Badge, Alert, Row, Col } from 'react-bootstrap';
import { FiPlus, FiEdit2, FiTrash2, FiShield, FiArrowLeft, FiCheck } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import api from '../services/api';

const PERMISSION_OPTIONS = [
    { label: 'Usuários', subject: 'User', description: 'Gerenciar usuários do sistema' },
    { label: 'Treinamentos', subject: 'Training', description: 'Criar e gerenciar treinamentos' },
    { label: 'Calendário', subject: 'Calendar', description: 'Visualizar calendário de treinamentos' },
    { label: 'Participantes', subject: 'Participant', description: 'Gerenciar cadastro de participantes' },
    { label: 'Salas', subject: 'Room', description: 'Gerenciar salas de treinamento' },
    { label: 'Relatórios', subject: 'Report', description: 'Acessar relatórios do sistema' },
    { label: 'Auditoria', subject: 'AuditLog', description: 'Visualizar logs de auditoria' },
    { label: 'Configurações', subject: 'SystemConfig', description: 'Alterar configurações do sistema' },
    { label: 'Perfis de Acesso', subject: 'Profile', description: 'Gerenciar perfis e permissões' },
    { label: 'Modo Tablet', subject: 'Tablet', description: 'Acesso exclusivo ao modo tablet' },
    { label: 'Dashboard', subject: 'Dashboard', description: 'Visualizar dashboard e métricas' },
    { label: 'Minha Conta', subject: 'MyAccount', description: 'Acessar e editar própria conta' }
];

const ProfileManagement = () => {
    const [profiles, setProfiles] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [editingProfile, setEditingProfile] = useState(null);
    const [formData, setFormData] = useState({ nome: '', descricao: '', home_page: '/training-list', permissions: [] });
    const [feedback, setFeedback] = useState({ type: '', text: '' });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchProfiles();
    }, []);

    const fetchProfiles = async () => {
        try {
            const response = await api.get('/profiles');
            setProfiles(response.data);
        } catch (error) {
            console.error('Erro ao buscar perfis', error);
            setFeedback({ type: 'danger', text: 'Erro ao carregar lista de perfis.' });
        }
    };

    const handleShow = (profile = null) => {
        if (profile) {
            setEditingProfile(profile);
            // Converter regras do backend para formato do formulário (lista de subjects)
            const currentPermissions = profile.rules
                .filter(r => r.action === 'manage')
                .map(r => r.subject);
            
            // Tratamento especial para 'all' (Admin)
            const isAdmin = profile.rules.some(r => r.subject === 'all');
            
            setFormData({
                nome: profile.nome,
                descricao: profile.descricao || '',
                home_page: profile.home_page || '/training-list',
                permissions: isAdmin ? PERMISSION_OPTIONS.map(p => p.subject) : currentPermissions
            });
        } else {
            setEditingProfile(null);
            setFormData({ nome: '', descricao: '', home_page: '/training-list', permissions: [] });
        }
        setShowModal(true);
        setFeedback({ type: '', text: '' });
    };

    const handleClose = () => setShowModal(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handlePermissionChange = (subject) => {
        setFormData(prev => {
            const exists = prev.permissions.includes(subject);
            if (exists) {
                return { ...prev, permissions: prev.permissions.filter(p => p !== subject) };
            } else {
                return { ...prev, permissions: [...prev.permissions, subject] };
            }
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        
        // Converter permissões selecionadas para formato CASL
        // Se todas as permissões forem selecionadas, podemos simplificar para 'manage all'? 
        // Por segurança, vamos manter explícito, a menos que seja explicitamente um Admin
        
        let rules = formData.permissions.map(subject => ({
            action: 'manage',
            subject: subject
        }));

        // Adicionar regras implícitas ou específicas se necessário
        // Por exemplo, todo mundo pode ler seu próprio perfil (geralmente tratado no backend/frontend logic)

        const payload = {
            nome: formData.nome,
            descricao: formData.descricao,
            home_page: formData.home_page,
            rules: rules
        };

        try {
            if (editingProfile) {
                await api.put(`/profiles/${editingProfile.id}`, payload);
                setFeedback({ type: 'success', text: 'Perfil atualizado com sucesso!' });
            } else {
                await api.post('/profiles', payload);
                setFeedback({ type: 'success', text: 'Perfil criado com sucesso!' });
            }
            fetchProfiles();
            handleClose();
        } catch (error) {
            console.error('Erro ao salvar perfil', error);
            const msg = error.response?.data?.msg || 'Erro ao salvar perfil.';
            setFeedback({ type: 'danger', text: msg });
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (profile) => {
        if (!window.confirm(`Tem certeza que deseja excluir o perfil "${profile.nome}"? Usuários vinculados perderão acesso.`)) return;

        try {
            await api.delete(`/profiles/${profile.id}`);
            setFeedback({ type: 'success', text: 'Perfil excluído com sucesso!' });
            fetchProfiles();
        } catch (error) {
            console.error('Erro ao excluir perfil', error);
            const msg = error.response?.data?.msg || 'Erro ao excluir perfil.';
            setFeedback({ type: 'danger', text: msg });
        }
    };

    return (
        <Container className="mt-4">
            <div className="mb-4">
                <Button 
                    variant="link" 
                    as={Link} 
                    to="/user-management" 
                    className="text-decoration-none p-0 mb-2 text-muted"
                >
                    <FiArrowLeft className="me-1" /> Voltar para Gestão de Usuários
                </Button>                
            </div>
            <div className="d-flex justify-content-between align-items-center">
                <h2 className="mb-4 fw-bold" style={{ fontFamily: 'Nunito, sans-serif', color: '#006064' }}>
                    Perfis de Acesso
                </h2>
                <Button variant="primary" onClick={() => handleShow()} className="fw-bold">
                    <FiPlus className="me-1" /> Novo Perfil
                </Button>
            </div>

            {feedback.text && <Alert variant={feedback.type} dismissible onClose={() => setFeedback({ type: '', text: '' })}>{feedback.text}</Alert>}

            <Card className="shadow-sm border-0">
                <Table responsive hover className="mb-0 align-middle">
                    <thead className="bg-light">
                        <tr>
                            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-4">Nome</th>
                            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Descrição</th>
                            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Permissões</th>
                            <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {profiles.map(profile => (
                            <tr key={profile.id}>
                                <td className="ps-4 fw-bold text-dark">{profile.nome}</td>
                                <td className="text-muted">{profile.descricao}</td>
                                <td>
                                    {profile.rules.some(r => r.subject === 'all') ? (
                                        <Badge bg="danger">Acesso Total (Admin)</Badge>
                                    ) : (
                                        <div className="d-flex gap-1 flex-wrap">
                                            {profile.rules.filter(r => r.action === 'manage').map((rule, idx) => (
                                                <Badge key={idx} bg="info" className="text-dark">
                                                    {PERMISSION_OPTIONS.find(p => p.subject === rule.subject)?.label || rule.subject}
                                                </Badge>
                                            ))}
                                        </div>
                                    )}
                                </td>
                                <td className="text-center">
                                    <Button variant="link" className="text-primary p-0 me-3" onClick={() => handleShow(profile)} title="Editar">
                                        <FiEdit2 size={18} />
                                    </Button>
                                    <Button variant="link" className="text-danger p-0" onClick={() => handleDelete(profile)} title="Excluir">
                                        <FiTrash2 size={18} />
                                    </Button>
                                </td>
                            </tr>
                        ))}
                        {profiles.length === 0 && (
                            <tr>
                                <td colSpan="4" className="text-center py-4 text-muted">
                                    Nenhum perfil cadastrado.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </Card>

            {/* Modal de Criação/Edição */}
            <Modal show={showModal} onHide={handleClose} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>{editingProfile ? 'Editar Perfil' : 'Novo Perfil'}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        <Row className="g-3">
                            <Col md={12}>
                                <Form.Group controlId="nome">
                                    <Form.Label>Nome do Perfil</Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        name="nome" 
                                        value={formData.nome} 
                                        onChange={handleChange} 
                                        required 
                                        placeholder="Ex: Supervisor"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={12}>
                                <Form.Group controlId="descricao">
                                    <Form.Label>Descrição</Form.Label>
                                    <Form.Control 
                                        as="textarea" 
                                        rows={2}
                                        name="descricao" 
                                        value={formData.descricao} 
                                        onChange={handleChange} 
                                        placeholder="Breve descrição das responsabilidades deste perfil"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={12}>
                                <Form.Group controlId="home_page">
                                    <Form.Label>Página Inicial (Home Page)</Form.Label>
                                    <Form.Select 
                                        name="home_page" 
                                        value={formData.home_page} 
                                        onChange={handleChange}
                                    >
                                        <option value="/dashboard">Dashboard (/dashboard)</option>
                                        <option value="/training-list">Lista de Treinamentos (/training-list)</option>
                                        <option value="/tablet-mode">Modo Tablet (/tablet-mode)</option>
                                        <option value="/my-account">Minha Conta (/my-account)</option>
                                    </Form.Select>
                                    <Form.Text className="text-muted">
                                        Página para onde o usuário será redirecionado após o login.
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                            <Col md={12}>
                                <Form.Label className="mb-2 fw-bold">Permissões de Acesso</Form.Label>
                                <Card className="bg-light border-0 p-3">
                                    <Row>
                                        {PERMISSION_OPTIONS.map((option) => (
                                            <Col md={6} key={option.subject} className="mb-2">
                                                <Form.Check 
                                                    type="switch"
                                                    id={`perm-${option.subject}`}
                                                    label={
                                                        <div>
                                                            <span className="fw-bold">{option.label}</span>
                                                            <br/>
                                                            <small className="text-muted">{option.description}</small>
                                                        </div>
                                                    }
                                                    checked={formData.permissions.includes(option.subject)}
                                                    onChange={() => handlePermissionChange(option.subject)}
                                                />
                                            </Col>
                                        ))}
                                    </Row>
                                </Card>
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleClose}>
                            Cancelar
                        </Button>
                        <Button variant="primary" type="submit" disabled={loading}>
                            {loading ? 'Salvando...' : 'Salvar Perfil'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </Container>
    );
};

export default ProfileManagement;
